import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ManhwaCard from '@/components/feature/ManhwaCard';
import useSeo from '@/hooks/useSeo';
import { manhwaList, getManhwaById, getGenreById } from '@/mocks/manhwa';

export default function DetailPage() {
  const { id = '' } = useParams();
  const manhwa = getManhwaById(id);

  useSeo({
    title: manhwa ? `韩漫《${manhwa.title}》- 漫画韩漫网址${getGenreById(manhwa.genre)?.name ?? ''}题材` : '漫画韩漫网址',
    description: manhwa ? `${manhwa.title}韩漫简介：${manhwa.summary} 标签：${manhwa.tags.join('、')}。` : '韩漫题材分类导航站。',
  });

  if (!manhwa) {
    return <Navigate to="/" replace />;
  }

  const genre = getGenreById(manhwa.genre);
  const related = manhwaList
    .filter((m) => m.genre === manhwa.genre && m.id !== manhwa.id)
    .concat(manhwaList.filter((m) => m.genre !== manhwa.genre))
    .slice(0, 4);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: manhwa.title,
            genre: genre?.name,
            author: { '@type': 'Person', name: manhwa.author },
            description: manhwa.summary,
            keywords: manhwa.tags.join(','),
            url: `https://suspensek.github.io/detail/${manhwa.id}`,
          }),
        }}
      />
      <Navbar />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* 面包屑 */}
          <nav aria-label="面包屑导航" className="mb-8">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li>
                <Link to={`/category/${manhwa.genre}`} className="hover:text-primary-600 whitespace-nowrap">
                  {genre?.name ?? ''}韩漫
                </Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-800 whitespace-nowrap truncate max-w-[10rem]">
                {manhwa.title}
              </li>
            </ol>
          </nav>

          {/* 主要内容区 */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="lg:sticky lg:top-24 rounded-2xl overflow-hidden border border-background-200 bg-background-50">
                <div className="aspect-[3/4]">
                  <img
                    src={manhwa.cover}
                    alt={`${manhwa.title}韩漫封面`}
                    title={`${manhwa.title} - 韩漫${genre?.name ?? ''}题材`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 xl:col-span-9">
              <article>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Link
                    to={`/category/${manhwa.genre}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap"
                  >
                    {genre?.name ?? ''}韩漫
                  </Link>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      manhwa.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-800'
                    }`}
                  >
                    {manhwa.status}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                    已更新 {manhwa.chapters} 话
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">{manhwa.title}</h1>

                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <span className="flex items-center gap-2 text-foreground-700 text-sm">
                    <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
                    <strong className="font-display-latin text-xl text-accent-600">{manhwa.rating.toFixed(1)}</strong>
                    分
                  </span>
                  <span className="flex items-center gap-2 text-foreground-600 text-sm">
                    <i className="ri-user-line" aria-hidden="true"></i> 作者：{manhwa.author}
                  </span>
                  <span className="flex items-center gap-2 text-foreground-600 text-sm">
                    <i className="ri-book-3-line" aria-hidden="true"></i> {manhwa.chapters}话
                  </span>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {manhwa.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-foreground-700 text-xs whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <h2 className="mt-10 font-heading text-lg md:text-xl text-foreground-950">内容简介</h2>
                <p className="mt-4 text-foreground-700 leading-relaxed text-sm md:text-base">{manhwa.summary}</p>
                <p className="mt-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                  这部{genre?.name ?? ''}韩漫以细腻的叙事和鲜明的人物塑造见长，适合喜欢{manhwa.tags.join('、')}元素的读者。若你是第一次接触该题材，它也是不错的入门之选。
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl bg-primary-50 border border-primary-200 p-5">
                  <div className="flex-1">
                    <p className="font-medium text-primary-800 text-sm md:text-base">想在漫画韩漫网址找到更多同类韩漫？</p>
                    <p className="mt-1 text-xs md:text-sm text-primary-700/80">
                      前往{genre?.name ?? ''}韩漫分类，浏览更多同题材精选内容。
                    </p>
                  </div>
                  <Link
                    to={`/category/${manhwa.genre}`}
                    className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium flex items-center whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    查看{genre?.name ?? ''}题材
                  </Link>
                </div>
              </article>
            </div>
          </div>

          {/* 相关推荐 */}
          <section className="mt-16 pt-10 border-t border-background-200" aria-label="相关韩漫推荐">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">你可能还喜欢</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <ManhwaCard key={m.id} manhwa={m} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}